<template>
  <Dialog
    v-model:visible="localDialogOpen"
    modal
    header="Account Aggregator Integration"
    :style="{ width: '500px' }"
    :closable="true"
    class="aa-integration-dialog"
  >
    <template #header>
      <div class="dialog-header">
        <i class="pi pi-link dialog-header-icon"></i>
        <span class="dialog-title">Account Aggregator Integration</span>
      </div>
    </template>

    <div class="aa-integration-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner">
          <i class="pi pi-spinner pi-spin"></i>
        </div>
        <h4>Processing Request</h4>
        <p>{{ loadingMessage }}</p>
        <div class="progress-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="error-state">
        <div class="error-icon">
          <i class="pi pi-exclamation-triangle"></i>
        </div>
        <h4>Integration Failed</h4>
        <p>{{ errorMessage }}</p>

        <!-- Show manual popup button if it's a popup-related error -->
        <div v-if="errorMessage.includes('popup') || errorMessage.includes('Popup')" class="manual-popup-section">
          <div class="manual-popup-card">
            <i class="pi pi-external-link"></i>
            <div class="manual-popup-content">
              <h5>Manual Portal Access</h5>
              <p>Your browser blocked the popup. Click below to open manually.</p>
              <Button
                label="Open AA Portal"
                icon="pi pi-external-link"
                @click="openManualPopup"
                class="p-button-primary"
                :disabled="!manualPopupUrl"
              />
            </div>
          </div>
        </div>

        <div class="error-actions">
          <Button
            label="Try Again"
            icon="pi pi-refresh"
            @click="resetAndRetry"
            class="p-button-outlined"
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            @click="closeDialog"
            class="p-button-text"
          />
        </div>
      </div>

      <!-- Success State -->
      <div v-else-if="integrationSuccess" class="success-state">
        <div class="success-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <h4>Integration Successful!</h4>
        <p>Your existing holdings have been fetched successfully from the Account Aggregator portal.</p>

        <div class="success-features">
          <div class="feature-item">
            <i class="pi pi-shield"></i>
            <span>Secure Transfer</span>
          </div>
          <div class="feature-item">
            <i class="pi pi-check"></i>
            <span>Data Verified</span>
          </div>
          <div class="feature-item">
            <i class="pi pi-sync"></i>
            <span>Portfolio Updated</span>
          </div>
        </div>

        <Button
          label="Close"
          icon="pi pi-times"
          @click="closeDialog"
          class="p-button-success close-success-btn"
        />
      </div>

      <!-- Initial State -->
      <div v-else class="initial-state">
        <div class="aa-hero">
          <div class="hero-icon">
            <i class="pi pi-building"></i>
          </div>
          <h4>Connect Your Financial Accounts</h4>
          <p>Securely fetch your existing investment holdings from banks and financial institutions through India's regulated Account Aggregator network.</p>
        </div>

        <div class="aa-benefits">
          <div class="benefit-card">
            <div class="benefit-icon secure">
              <i class="pi pi-shield"></i>
            </div>
            <div class="benefit-content">
              <h6>RBI Regulated</h6>
              <small>Secure & government approved</small>
            </div>
          </div>

          <div class="benefit-card">
            <div class="benefit-icon realtime">
              <i class="pi pi-clock"></i>
            </div>
            <div class="benefit-content">
              <h6>Real-time Data</h6>
              <small>Live portfolio information</small>
            </div>
          </div>

          <div class="benefit-card">
            <div class="benefit-icon complete">
              <i class="pi pi-chart-line"></i>
            </div>
            <div class="benefit-content">
              <h6>Complete View</h6>
              <small>All accounts in one place</small>
            </div>
          </div>
        </div>

        <div class="aa-actions">
          <Button
            label="Get Holdings"
            icon="pi pi-download"
            @click="initiateAAIntegration"
            class="p-button-primary get-holdings-btn"
            :loading="isLoading"
          />
          <Button
            label="Maybe Later"
            icon="pi pi-times"
            @click="closeDialog"
            class="p-button-outlined p-button-secondary"
          />
        </div>

        <div class="security-note">
          <i class="pi pi-info-circle"></i>
          <span>Your data is encrypted and never stored on our servers</span>
        </div>
      </div>
    </div>
  </Dialog>
</template><script setup>
import { ref, watch } from 'vue';
import { useUserStore } from '@/store/user-store';
import { Dialog, Button } from 'primevue';

const props = defineProps({
  isAccountAggregatorDialogOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:isAccountAggregatorDialogOpen']);

const userStore = useUserStore();

// Local state
const localDialogOpen = ref(false);
const isLoading = ref(false);
const loadingMessage = ref('');
const errorMessage = ref('');
const integrationSuccess = ref(false);
const manualPopupUrl = ref('');

// Two-way binding handled through watch functions

// Watch for prop changes
watch(() => props.isAccountAggregatorDialogOpen, (newVal) => {
  localDialogOpen.value = newVal;
  if (newVal) {
    resetState();
  }
});

// Watch for local changes
watch(localDialogOpen, (newVal) => {
  emit('update:isAccountAggregatorDialogOpen', newVal);
});

// AA popup function - add to window object
const addAAScript = () => {
  if (!window.redirectToAA) {
    const script = document.createElement('script');
    script.textContent = `
      var aaWindowObjectReference = null;
      window.redirectToAA = function(aaRedirectionURL) {
        console.log('redirectToAA called with URL:', aaRedirectionURL);
        if(aaWindowObjectReference == null || aaWindowObjectReference.closed) {
          var width = screen.availWidth * 2 / 3;
          var height = screen.availHeight * 2 / 3;
          var left = (screen.availWidth - width) / 2;
          var top = (screen.availHeight - height) / 2;

          console.log('Opening popup with dimensions:', {width, height, left, top});
          aaWindowObjectReference = window.open(aaRedirectionURL, "aa",
            'resizable=yes, width=' + width + ', height=' + height + ', top=' + top + ', left=' + left);

          if (aaWindowObjectReference) {
            console.log('Popup window created successfully');
            aaWindowObjectReference.focus();
            return aaWindowObjectReference;
          } else {
            console.error('Failed to create popup window - likely blocked by browser');
            return null;
          }
        } else {
          console.log('Focusing existing popup window');
          aaWindowObjectReference.focus();
          return aaWindowObjectReference;
        }
      };
    `;
    document.head.appendChild(script);
    console.log('AA script injected into page');
  } else {
    console.log('redirectToAA function already exists');
  }
};

// Direct popup function (more reliable for user-initiated events)
const openAAPopup = (url) => {
  try {
    console.log('Attempting direct popup for URL:', url);
    const width = screen.availWidth * 2 / 3;
    const height = screen.availHeight * 2 / 3;
    const left = (screen.availWidth - width) / 2;
    const top = (screen.availHeight - height) / 2;

    const popup = window.open(
      url,
      'aa_popup',
      `resizable=yes,width=${width},height=${height},top=${top},left=${left},scrollbars=yes,status=yes,toolbar=no,menubar=no`
    );

    if (popup) {
      popup.focus();
      console.log('Direct popup opened successfully');
      return popup;
    } else {
      console.warn('Direct popup was blocked or failed');
      return null;
    }
  } catch (error) {
    console.error('Error opening direct popup:', error);
    return null;
  }
};

const resetState = () => {
  isLoading.value = false;
  loadingMessage.value = '';
  errorMessage.value = '';
  integrationSuccess.value = false;
  manualPopupUrl.value = '';
};

const openManualPopup = () => {
  if (manualPopupUrl.value) {
    const popup = openAAPopup(manualPopupUrl.value);
    if (popup) {
      integrationSuccess.value = true;
      errorMessage.value = '';
    }
  }
};

const resetAndRetry = () => {
  resetState();
  initiateAAIntegration();
};

const closeDialog = () => {
  localDialogOpen.value = false;
  resetState();
};

const initiateAAIntegration = async () => {
  try {
    isLoading.value = true;
    loadingMessage.value = 'Authenticating and getting redirection URL...';
    errorMessage.value = '';

    // Get phone number and PAN from dashboard data
    const phoneNumber = userStore.dashboardData?.phoneNumber;
    const pan = userStore.dashboardData?.pan || '';

    console.log('Initiating AA authentication and redirect with:', { phoneNumber, pan });

    // Single call to authenticate and get redirection URL
    const redirectResponse = await userStore.authenticateAndRedirectToAA({
      phoneNumber,
      pan
    });

    console.log('Redirect response:', redirectResponse);
    console.log('success:', redirectResponse.success);
    console.log('Redirection URL:', redirectResponse.redirectionurl);

    if (redirectResponse.success && redirectResponse.redirectionurl) {
      loadingMessage.value = 'Opening Account Aggregator portal...';

      // Store URL for manual popup fallback
      manualPopupUrl.value = redirectResponse.redirectionurl;

      // Step 3: Open AA popup with enhanced error handling
      console.log('About to open popup with URL:', redirectResponse.redirectionurl);

      try {
        // Try direct popup first (more reliable for user-triggered events)
        const popup = openAAPopup(redirectResponse.redirectionurl);

        if (popup) {
          console.log('Popup opened successfully');
          isLoading.value = false;
          integrationSuccess.value = true;
        } else {
          console.error('Popup was blocked or failed to open, trying script injection method');

          // Fallback to script injection method immediately
          addAAScript();

          setTimeout(() => {
            try {
              if (window.redirectToAA) {
                console.log('Trying script-based popup...');
                const result = window.redirectToAA(redirectResponse.redirectionurl);

                if (result) {
                  console.log('Script popup opened successfully');
                  isLoading.value = false;
                  integrationSuccess.value = true;
                } else {
                  console.error('Script popup was also blocked');
                  isLoading.value = false;
                  errorMessage.value = 'Popup blocked by browser. Please enable popups for this site and try again.';
                }
              } else {
                console.error('redirectToAA function not available');
                isLoading.value = false;
                errorMessage.value = 'Failed to load AA popup function. Please check browser settings.';
              }
            } catch (scriptError) {
              console.error('Script popup error:', scriptError);
              isLoading.value = false;
              errorMessage.value = 'Popup blocked by browser. Please enable popups for this site and try again.';
            }
          }, 100);
        }
      } catch (popupError) {
        console.error('Direct popup failed, trying script injection method:', popupError);

        // Fallback to script injection method
        addAAScript();

        setTimeout(() => {
          try {
            if (window.redirectToAA) {
              console.log('Trying script-based popup...');
              const result = window.redirectToAA(redirectResponse.redirectionurl);

              if (result) {
                console.log('Script popup opened successfully');
                isLoading.value = false;
                integrationSuccess.value = true;
              } else {
                console.error('Script popup was also blocked');
                isLoading.value = false;
                errorMessage.value = 'Popup blocked by browser. Please enable popups for this site and try again.';
              }
            } else {
              console.error('redirectToAA function not available');
              isLoading.value = false;
              errorMessage.value = 'Failed to load AA popup function. Please check browser settings.';
            }
          } catch (scriptError) {
            console.error('Script popup error:', scriptError);
            isLoading.value = false;
            errorMessage.value = 'Popup blocked by browser. Please enable popups for this site and try again.';
          }
        }, 100);
      }

    } else {
      throw new Error('Failed to get redirection URL from Account Aggregator');
    }

  } catch (error) {
    console.error('AA Integration error:', error);
    isLoading.value = false;
    errorMessage.value = error.message || 'Failed to integrate with Account Aggregator. Please try again.';

    // Show user-friendly error based on error type
    if (error.message?.includes('network') || error.message?.includes('fetch')) {
      errorMessage.value = 'Network error. Please check your connection and try again.';
    } else if (error.message?.includes('authentication')) {
      errorMessage.value = 'Authentication failed. Please verify your details and try again.';
    } else if (error.message?.includes('Popup blocked')) {
      errorMessage.value = 'Popup blocked by browser. Please enable popups for this site in your browser settings and try again.';
    } else if (error.message?.includes('popup')) {
      errorMessage.value = 'Unable to open Account Aggregator portal. Please check your browser popup settings.';
    }
  }
};
</script>

<style scoped>
/* Dialog Styling */
.aa-integration-dialog :deep(.p-dialog) {
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.aa-integration-dialog :deep(.p-dialog-header) {
  padding: 0;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.aa-integration-dialog :deep(.p-dialog-content) {
  padding: 0;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  position: relative;
}

.dialog-header-icon {
  font-size: 1.5rem;
  margin-right: 12px;
  opacity: 0.9;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.aa-integration-content {
  padding: 32px 28px;
  min-height: 200px;
  background: #ffffff;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 20px;
  padding: 40px 20px;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.loading-spinner i {
  font-size: 1.8rem;
  color: white;
}

.loading-state h4 {
  margin: 0;
  color: #2d3748;
  font-size: 1.25rem;
  font-weight: 600;
}

.loading-state p {
  margin: 0;
  color: #64748b;
  font-size: 1rem;
  max-width: 300px;
  line-height: 1.5;
}

.progress-dots {
  display: flex;
  gap: 6px;
}

.progress-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e0;
  animation: pulse 1.5s ease-in-out infinite;
}

.progress-dots span:nth-child(1) { animation-delay: 0s; }
.progress-dots span:nth-child(2) { animation-delay: 0.3s; }
.progress-dots span:nth-child(3) { animation-delay: 0.6s; }

@keyframes pulse {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 20px;
  padding: 40px 20px;
}

.error-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon i {
  font-size: 1.8rem;
  color: white;
}

.error-state h4 {
  margin: 0;
  color: #2d3748;
  font-size: 1.25rem;
  font-weight: 600;
}

.error-state p {
  margin: 0;
  color: #64748b;
  max-width: 350px;
  line-height: 1.5;
}

.manual-popup-section {
  width: 100%;
  margin: 16px 0;
}

.manual-popup-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
}

.manual-popup-card > i {
  color: #4299e1;
  font-size: 1.5rem;
  margin-top: 4px;
}

.manual-popup-content h5 {
  margin: 0 0 8px 0;
  color: #2d3748;
  font-size: 1rem;
  font-weight: 600;
}

.manual-popup-content p {
  margin: 0 0 16px 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.4;
}

.error-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

/* Success State */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 24px;
  padding: 40px 20px;
}

.success-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon i {
  font-size: 2rem;
  color: white;
}

.success-state h4 {
  margin: 0;
  color: #2d3748;
  font-size: 1.4rem;
  font-weight: 700;
}

.success-state p {
  margin: 0;
  color: #64748b;
  max-width: 350px;
  line-height: 1.5;
}

.success-features {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f0fff4;
  border-radius: 8px;
  border: 1px solid #c6f6d5;
  min-width: 80px;
}

.feature-item i {
  color: #38a169;
  font-size: 1.2rem;
}

.feature-item span {
  font-size: 0.8rem;
  font-weight: 500;
  color: #276749;
}

.close-success-btn {
  min-width: 120px;
}

/* Initial State */
.initial-state {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.aa-hero {
  text-align: center;
  padding-bottom: 8px;
}

.hero-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px auto;
}

.hero-icon i {
  font-size: 2.2rem;
  color: white;
}

.aa-hero h4 {
  margin: 0 0 16px 0;
  color: #2d3748;
  font-size: 1.4rem;
  font-weight: 700;
}

.aa-hero p {
  margin: 0;
  color: #64748b;
  line-height: 1.6;
  font-size: 1rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.aa-benefits {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.benefit-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e0;
}

.benefit-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  flex-shrink: 0;
}

.benefit-icon.secure {
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
}

.benefit-icon.realtime {
  background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
}

.benefit-icon.complete {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.benefit-content h6 {
  margin: 0 0 4px 0;
  color: #2d3748;
  font-size: 1rem;
  font-weight: 600;
}

.benefit-content small {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.3;
}

.aa-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.aa-actions .p-button {
  min-width: 140px;
  padding: 12px 20px;
  font-weight: 600;
  border-radius: 8px;
}

.get-holdings-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.get-holdings-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.security-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  margin-top: 4px;
}

.security-note i {
  color: #0284c7;
  font-size: 1rem;
}

.security-note span {
  color: #0c4a6e;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .aa-integration-dialog :deep(.p-dialog) {
    width: 95vw !important;
    margin: 10px;
  }

  .aa-integration-content {
    padding: 24px 20px;
  }

  .aa-hero h4 {
    font-size: 1.25rem;
  }

  .aa-hero p {
    font-size: 0.95rem;
  }

  .benefit-card {
    padding: 14px 16px;
  }

  .aa-actions {
    flex-direction: column;
  }

  .aa-actions .p-button {
    width: 100%;
  }

  .success-features {
    gap: 12px;
  }

  .feature-item {
    min-width: 70px;
    padding: 8px;
  }

  .error-actions {
    flex-direction: column;
    width: 100%;
  }

  .error-actions .p-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .dialog-header {
    padding: 16px 20px;
  }

  .dialog-title {
    font-size: 1.1rem;
  }

  .hero-icon {
    width: 70px;
    height: 70px;
  }

  .hero-icon i {
    font-size: 2rem;
  }
}
</style>
